import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import {
  QueuesListQuery,
  type QueueRow,
  type QueueJobStatus,
  type QueuesListData,
} from "@/admin/entities/queues";
import { QUEUES_PAGE_SIZE, QUEUES_POLL_INTERVAL } from "./config";

/**
 * Список очередей со счётчиками. Фильтр и сортировка — на клиенте: список
 * приходит целиком, и так поиск реагирует на каждое нажатие без похода на
 * сервер (тот же приём, что в референсной админке).
 */
export function useQueuesList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filter = searchParams.get("filter") || "";
  const sort = searchParams.get("sort") || "name";

  const setFilter = (value: string) =>
    setSearchParams({ sort, filter: value }, { replace: true });
  const setSort = (value: string) =>
    setSearchParams({ sort: value, filter }, { replace: true });

  const { data, loading, error } = useQuery<QueuesListData>(QueuesListQuery, {
    fetchPolicy: "cache-and-network",
    pollInterval: QUEUES_POLL_INTERVAL,
    variables: { page: 0, rowsPerPage: QUEUES_PAGE_SIZE },
  });

  const nodes = useMemo<QueueRow[]>(() => {
    let result: QueueRow[] = data?.queues?.nodes
      ? Array.from(data.queues.nodes)
      : [];

    if (filter) {
      const needle = filter.toLowerCase();
      result = result.filter((queue) =>
        queue.name.toLowerCase().includes(needle)
      );
    }

    if (sort === "name") {
      // Сервер отдаёт очереди в порядке -createdAt, так что без явной
      // сортировки вариант «По названию» не делал ничего
      result = [...result].sort((a, b) => a.name.localeCompare(b.name));
    } else {
      const key = sort as QueueJobStatus;
      result = [...result].sort((a, b) => b.jobCounts[key] - a.jobCounts[key]);
    }

    return result;
  }, [data, filter, sort]);

  return { nodes, loading, error, filter, setFilter, sort, setSort };
}
